import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../../context/API';
import axios from 'axios';
import { passwordResetAPI } from '../../APIs/passwordResetAPI';
import notification from '../../commonComponents/Notification/Notification';
import { getDashboardUrl, getRoleFromHostname, MAX_OTP_REQUESTS } from '../../components/auth/constants';
import { getProfileEndpoint } from '../../utils/profileApi';
import type {
  LoginApiResponse,
  LoginData,
  LoginDataMerchant,
  LoginDataAdmin,
  LoginDataManager,
  LoginDataStaff,
} from '../../components/auth/LoginPage/types';
import {
  isLoginDataAdmin,
  isLoginDataManager,
  isLoginDataStaff,
  isLoginDataOtpSent,
} from '../../components/auth/LoginPage/loginResponseGuards';

interface LoginCredentials {
  emailOrUsername: string;
  password: string;
}

interface AuthState {
  isLoggedIn: boolean;
  isLoggingIn: boolean;
  loginData: any | null;
  role: string;
  isResendingOTP: boolean;
  isVerifyingOTP: boolean;
  isResettingPassword: boolean;
  isVerifyingCode: boolean;
  error: string | null;
}

// Initialize state from localStorage if available
const getInitialState = (): AuthState => {
  if (typeof window !== 'undefined') {
    const storedToken = localStorage.getItem('access_token');
    const storedRole = localStorage.getItem('role');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedRole) {
      return {
        isLoggedIn: true,
        isLoggingIn: false,
        loginData: storedUser ? JSON.parse(storedUser) : null,
        role: storedRole,
        isResendingOTP: false,
        isVerifyingOTP: false,
        isResettingPassword: false,
        isVerifyingCode: false,
        error: null,
      };
    }
  }
  
  return {
    isLoggedIn: false,
    isLoggingIn: false,
    loginData: null,
    role: '',
    isResendingOTP: false,
    isVerifyingOTP: false,
    isResettingPassword: false,
    isVerifyingCode: false,
    error: null,
  };
};

const initialState: AuthState = getInitialState();

type LoginDataWithJwt = LoginDataMerchant | LoginDataAdmin | LoginDataManager | LoginDataStaff;

const getRoleAndUserFromPayload = (data: LoginDataWithJwt): { role: string; user: Record<string, unknown> } => {
  if (isLoginDataAdmin(data)) {
    return { role: data.admin.role, user: data.admin as unknown as Record<string, unknown> };
  }
  if (isLoginDataManager(data)) {
    return { role: data.manager.role, user: data.manager as unknown as Record<string, unknown> };
  }
  if (isLoginDataStaff(data)) {
    return { role: data.staff.role, user: data.staff as unknown as Record<string, unknown> };
  }
  return { role: (data as LoginDataMerchant).merchant.role, user: (data as LoginDataMerchant).merchant as unknown as Record<string, unknown> };
};

// Async Thunks
export const login = createAsyncThunk(
  'auth/login',
  async (
    { data, hostname, navigate }: { data: LoginCredentials; hostname: string; navigate: (path: string, options?: { replace?: boolean }) => void },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const user = {
        emailOrUsername: data.emailOrUsername,
        password: data.password,
        source: 'web',
      };

      const { data: responseData } = await API.post<LoginApiResponse>('/portal/login', user, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });

      const payload: LoginData | undefined = responseData?.data;
      if (!payload) {
        const errorMessage = responseData?.message || 'Invalid login response';
        notification.error('Login Failed', errorMessage);
        return rejectWithValue(errorMessage);
      }

      // 3) OTP sent response – no jwt, only otp + phone → go to OTP screen
      if (isLoginDataOtpSent(payload)) {
        dispatch(setLoginData({ phone: payload.phone }));
        dispatch(setRole(getRoleFromHostname(hostname)));
        navigate('/otpverify');
        return { data: { phone: payload.phone }, role: getRoleFromHostname(hostname) };
      }

      // 1) Merchant or 2) Admin direct login – must have jwt
      if (!('jwt' in payload) || !payload.jwt) {
        const errorMessage = responseData?.message || 'Invalid login response';
        notification.error('Login Failed', errorMessage);
        return rejectWithValue(errorMessage);
      }

      const token = payload.jwt;
      const roleAndUser = getRoleAndUserFromPayload(payload as LoginDataWithJwt);
      const role = roleAndUser.role;
      const loginUser = roleAndUser.user;

      dispatch(setLoginData(loginUser));
      dispatch(setRole(role));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      localStorage.setItem('access_token', token);
      localStorage.setItem('user', JSON.stringify(loginUser));
      localStorage.setItem('role', role);

      let userToUse = loginUser as Record<string, unknown>;
      if (role === 'merchant_manager' || role === 'merchant_staff') {
        try {
          const endpoint = getProfileEndpoint(role);
          const profileRes = await API.get(endpoint, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const profileData = profileRes?.data?.data;
          if (profileData) {
            userToUse = profileData as Record<string, unknown>;
            localStorage.setItem('user', JSON.stringify(profileData));
            dispatch(setLoginData(profileData));
          }
        } catch (err) {
          console.error('Profile fetch after login failed:', err);
        }
      }

      if (role === 'merchant' || role === 'merchant_manager' || role === 'merchant_staff') {
        const hasOtp = (userToUse.otp && userToUse.phone) || ((loginUser as Record<string, unknown>).otp && (loginUser as Record<string, unknown>).phone);
        if (hasOtp) {
          navigate('/otpverify');
        } else {
          notification.success('Login Successful', 'You are now Signed in.');
          dispatch(setLoggedIn(true));
          const dashboardUrl = getDashboardUrl(token, role, hostname);
          const isExternalUrl = dashboardUrl.startsWith('http') && !dashboardUrl.includes(window.location.hostname);
          if (isExternalUrl) {
            window.location.href = dashboardUrl;
          } else {
            navigate('/dashboard');
          }
        }
      } else {
        notification.success('Login Successful', 'You are now Signed in.');
        dispatch(setLoggedIn(true));
        navigate('/dashboard', { replace: true });
      }

      return { data: userToUse, role };
    } catch (error: any) {
      console.log('Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unable to login';
      notification.error('Login Failed', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async (
    { phone, otp, navigate }: { phone: string; otp: string[]; navigate?: (path: string) => void },
    { rejectWithValue, dispatch }
  ) => {
    if (!phone?.trim()) {
      notification.error('Phone Number Required', 'Phone number is required for OTP verification.');
      return rejectWithValue('Phone number is required');
    }

    const otpString = otp.join('');
    if (otpString.length !== 6) {
      notification.error('Invalid OTP', 'Please enter a 6-digit OTP.');
      return rejectWithValue('Invalid OTP');
    }

    try {
      const res = await API.post('/merchant/verify-otp', {
        phone: phone.trim(),
        otp: otpString,
      });

      if (res.data.status === 200) {
        const { jwt: token, ...userData } = res.data.data;
        const role = localStorage.getItem('role') || 'merchant';

        localStorage.setItem('access_token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('role', role);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        dispatch(setLoggedIn(true));
        notification.success('Verification Successful', 'Your phone number and OTP have been verified.');

        navigate ? navigate('/dashboard') : (window.location.href = '/dashboard');
        return { token, userData };
      }

      notification.error('Invalid OTP', 'The OTP you entered is incorrect. Please try again.');
      return rejectWithValue('Invalid OTP');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'An error occurred while verifying the OTP. Please try again.';
      notification.error('Verification Failed', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const resendOTP = createAsyncThunk(
  'auth/resendOTP',
  async (
    { phone, otpRequestCount }: { phone: string; otpRequestCount: number },
    { rejectWithValue }
  ) => {
    if (!phone?.trim()) {
      notification.error('Phone Number Required', 'Phone number is required to resend OTP.');
      return rejectWithValue('Phone number is required');
    }

    if (otpRequestCount >= MAX_OTP_REQUESTS) {
      notification.error('Too Many Requests', `You have reached the maximum of ${MAX_OTP_REQUESTS} requests. Please wait 5 minutes.`);
      return rejectWithValue('Too many requests');
    }

    try {
      await API.post('/merchant/resend-otp', { phone: phone.trim() });
      notification.info('OTP Resent', 'A new OTP has been sent to your phone number.');
      return { success: true };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'An error occurred while resending the OTP. Please try again.';
      notification.error('Resend Failed', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string) => {
    try {
      await passwordResetAPI.requestPasswordReset(email.trim());
    } catch (error: any) {
      console.error('Password reset error:', error);
    }
    // Always show success for security (prevent email enumeration)
    notification.success('Password Reset Link Sent', "If an account with that email exists, we've sent a password reset link.");
    return { success: true };
  }
);

export const verifyResetCode = createAsyncThunk(
  'auth/verifyResetCode',
  async ({ email, code }: { email: string; code: string }, { rejectWithValue }) => {
    if (!code || code.length !== 6) {
      notification.error('Invalid Code', 'Please enter a 6-digit code.');
      return rejectWithValue('Invalid code');
    }

    try {
      const response = await passwordResetAPI.verifyResetOtp(email, code);
      
      if (response.status === 'success' || response.status === 200) {
        notification.success('Code Verified', response.data?.message || 'Please enter your new password.');
        return { success: true };
      }
      return rejectWithValue('Verification failed');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'The OTP you entered is incorrect or has expired. Please try again.';
      notification.error('Verification Failed', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (
    { email, password, confirmPassword, navigate }: 
    { email: string; password: string; confirmPassword?: string; navigate: (path: string) => void },
    { rejectWithValue }
  ) => {
    try {
      await passwordResetAPI.resetPassword(email, password, confirmPassword);
      notification.success('Password Reset Successful', 'Your password has been updated successfully.');
      navigate('/login');
      return { success: true };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to reset password. Please try again.';
      const requiresVerification = errorMessage.toLowerCase().includes('verify') || errorMessage.toLowerCase().includes('otp');
      
      notification.error(requiresVerification ? 'Verification Required' : 'Reset Failed', 
        requiresVerification ? 'Please verify the reset code before changing password.' : errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const resetPasswordWithToken = createAsyncThunk(
  'auth/resetPasswordWithToken',
  async (
    { token, password, navigate }: 
    { token: string; password: string; navigate: (path: string) => void },
    { rejectWithValue }
  ) => {
    try {
      await passwordResetAPI.resetPassword(token, password);
      notification.success('Password Updated', 'Your password has been updated successfully.');
      navigate('/login');
      return { success: true };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to reset password. Please try again.';
      notification.error('Reset Failed', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const validateResetToken = createAsyncThunk(
  'auth/validateResetToken',
  async (resetToken: string, { rejectWithValue }) => {
    try {
      await passwordResetAPI.validateResetToken(resetToken);
      return { isValid: true };
    } catch (error: any) {
      notification.error('Invalid Reset Link', 'This password reset link is invalid or has expired.');
      return rejectWithValue('Invalid token');
    }
  }
);

// Auth Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.isLoggedIn = action.payload;
    },
    setLoginData: (state, action: PayloadAction<any>) => {
      state.loginData = action.payload;
    },
    setRole: (state, action: PayloadAction<string>) => {
      state.role = action.payload;
    },
    logout: (state) => {
      state.isLoggedIn = false;
      state.loginData = null;
      state.role = '';
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      localStorage.removeItem('role');
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoggingIn = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoggingIn = false;
        // State is already updated via dispatch(setLoggedIn) in thunk
        // But ensure it's set here as well for consistency
        if (action.payload) {
          state.loginData = action.payload.data;
          state.role = action.payload.role;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoggingIn = false;
        state.error = action.payload as string;
      });

    // Verify OTP
    builder
      .addCase(verifyOTP.pending, (state) => {
        state.isVerifyingOTP = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.isVerifyingOTP = false;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isVerifyingOTP = false;
        state.error = action.payload as string;
      });

    // Resend OTP
    builder
      .addCase(resendOTP.pending, (state) => {
        state.isResendingOTP = true;
        state.error = null;
      })
      .addCase(resendOTP.fulfilled, (state) => {
        state.isResendingOTP = false;
      })
      .addCase(resendOTP.rejected, (state, action) => {
        state.isResendingOTP = false;
        state.error = action.payload as string;
      });

    // Forgot Password - handled in thunk, no state updates needed

    // Verify Reset Code
    builder
      .addCase(verifyResetCode.pending, (state) => {
        state.isVerifyingCode = true;
        state.error = null;
      })
      .addCase(verifyResetCode.fulfilled, (state) => {
        state.isVerifyingCode = false;
      })
      .addCase(verifyResetCode.rejected, (state, action) => {
        state.isVerifyingCode = false;
        state.error = action.payload as string;
      });

    // Reset Password
    builder
      .addCase(resetPassword.pending, (state) => {
        state.isResettingPassword = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isResettingPassword = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isResettingPassword = false;
        state.error = action.payload as string;
      });
  },
});

export const { setLoggedIn, setLoginData, setRole, logout, clearError } = authSlice.actions;
export default authSlice.reducer;
